// src/llmConnections/constants.js
//
// Provider/field catalogue for LLM connections, matching the real backend
// contract (architecture_scaffold/presentation/shared/connections/schemas/
// register_llm_connection_request.py, infrastructure/shared/connections/
// {ollama,azure_openai}_connection_tester.py).
//
// Field shape: { name, label, type: "text"|"password"|"select", required,
// placeholder?, helperText?, options? (for type:"select") } — same shape
// connectors/constants.js uses, so RegisterLlmConnectionModal can reuse the
// same dynamic-field-rendering pattern as connectors/components/
// RegisterConnectionModal.jsx. One addition on top of that shared shape:
// showIf(values) — an optional predicate; RegisterLlmConnectionModal only
// renders/validates a field when this is absent or returns true, which is
// how a provider can offer a choice (e.g. Ollama's authType) that swaps in
// a different set of fields instead of always showing all of them at once.
// authType/username/password/verifySsl are Ollama-only — matches
// infrastructure/shared/platform/ai_capability/{langchain,openai_sdk,
// crewai}_client_factory.py's Ollama builders (all three now share the same
// auth priority: api_key as a bearer header, else config.username/password
// as HTTP basic, else no auth; verify_ssl defaults true). Azure OpenAI's
// builders take only api_key and have no basic-auth or SSL-skip path, so
// don't add these fields there — they'd never be read.

const OLLAMA_AUTH_TYPE_OPTIONS = [
  { value: "none", label: "None" },
  { value: "api_key", label: "API key (Bearer token)" },
  { value: "basic", label: "Username & password (HTTP Basic)" },
];

const SSL_VERIFY_OPTIONS = [
  { value: "true", label: "Verify SSL certificate (recommended)" },
  { value: "false", label: "Skip certificate verification (insecure)" },
];

export const LLM_PURPOSE_OPTIONS = [
  { value: "map", label: "Map phase only" },
  { value: "reduce", label: "Reduce phase only" },
  { value: "both", label: "Both phases" },
];

// Who can see/use the connection, and — per RegisterLlmConnectionRequest's
// own docs — what permission registering at that level requires:
//   org      — every org member; needs connections:write held org-wide
//   account  — everyone reachable via account_id; needs connections:write
//              on that account (org-wide grant also satisfies it)
//   project  — everyone reachable via engagement_id; needs connections:write
//              on that project (account/org-wide grant also satisfies it)
//   personal — only the registering user; needs no permission grant at all
export const LLM_LEVEL_OPTIONS = [
  { value: "personal", label: "Personal — only you" },
  { value: "project", label: "Project — everyone on a project" },
  { value: "account", label: "Account — everyone on an account" },
  { value: "org", label: "Organization — everyone in your org" },
];

export const LLM_PROVIDERS = [
  {
    value: "ollama",
    label: "Ollama",
    initials: "OL",
    fields: [
      { name: "name", label: "Connection name", type: "text", required: true, placeholder: "e.g. Local Ollama" },
      { name: "baseUrl", label: "Base URL", type: "text", required: true, placeholder: "http://localhost:11434" },
      { name: "model", label: "Model", type: "text", required: true, placeholder: "llama3.1:8b" },
      { name: "authType", label: "Authentication", type: "select", required: true, options: OLLAMA_AUTH_TYPE_OPTIONS },
      {
        name: "apiKey",
        label: "API key",
        type: "password",
        required: true,
        helperText: "Sent as an Authorization: Bearer header.",
        showIf: (v) => v.authType === "api_key",
      },
      {
        name: "username",
        label: "Username",
        type: "text",
        required: true,
        showIf: (v) => v.authType === "basic",
      },
      {
        name: "password",
        label: "Password",
        type: "password",
        required: true,
        helperText: "Sent via HTTP Basic auth.",
        showIf: (v) => v.authType === "basic",
      },
      { name: "verifySsl", label: "SSL certificate", type: "select", required: true, options: SSL_VERIFY_OPTIONS },
      { name: "purpose", label: "Purpose", type: "select", required: true, options: LLM_PURPOSE_OPTIONS },
    ],
  },
  {
    value: "azure_openai",
    label: "Azure OpenAI",
    initials: "AO",
    fields: [
      { name: "name", label: "Connection name", type: "text", required: true, placeholder: "e.g. Azure GPT-5 mini" },
      { name: "endpoint", label: "Endpoint", type: "text", required: true, placeholder: "https://your-resource.openai.azure.com/" },
      { name: "apiKey", label: "API key", type: "password", required: true },
      { name: "deployment", label: "Deployment name", type: "text", required: true, placeholder: "gpt-5-mini" },
      { name: "apiVersion", label: "API version", type: "text", required: false, placeholder: "2024-12-01-preview" },
      { name: "purpose", label: "Purpose", type: "select", required: true, options: LLM_PURPOSE_OPTIONS },
    ],
  },
];

export function getLlmProvider(providerValue) {
  return LLM_PROVIDERS.find((p) => p.value === providerValue);
}
